/* Version: 3.5.137 */
'use client';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import 'react';
import PropTypes from 'prop-types';
import { safeRest, noop } from '../../utils/functions.js';
import safeCustomStyle from '../../utils/safeCustomStyle.js';
import Body from './Body.js';
import { ACCORDION_VARIANT } from './constants.js';
import Header from './Header.js';
import { InnerWrapper, SecondaryAccordionWrapper, PrimaryAccordionWrapper } from './styledComponents.js';
import useAccordion from './useAccordion.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import useDerivedState from '../../hooks/useDerivedState.js';
import useId from '../../hooks/useId.js';
import useTheme from '../../hooks/useTheme.js';

var _excluded = ["id", "title", "titleAs", "onToggle", "ariaLabel", "children", "variant", "customStyle", "expandIcon", "collapseIcon", "expandedByDefault", "isExpanded"];
var Accordion = function Accordion(_ref) {
  var controlledId = _ref.id,
    title = _ref.title,
    _ref$titleAs = _ref.titleAs,
    titleAs = _ref$titleAs === undefined ? 'span' : _ref$titleAs,
    _ref$onToggle = _ref.onToggle,
    onToggle = _ref$onToggle === undefined ? noop : _ref$onToggle,
    ariaLabel = _ref.ariaLabel,
    children = _ref.children,
    _ref$variant = _ref.variant,
    variant = _ref$variant === undefined ? ACCORDION_VARIANT.PRIMARY : _ref$variant,
    _ref$customStyle = _ref.customStyle,
    customStyle = _ref$customStyle === undefined ? {
      expandablePanelStyle: {},
      headerWrapperStyle: {},
      titleStyle: {},
      bodyStyle: {}
    } : _ref$customStyle,
    expandIcon = _ref.expandIcon,
    collapseIcon = _ref.collapseIcon,
    _ref$expandedByDefaul = _ref.expandedByDefault,
    defaultExpanded = _ref$expandedByDefaul === undefined ? false : _ref$expandedByDefaul,
    controlledExpanded = _ref.isExpanded,
    rest = _objectWithoutProperties(_ref, _excluded);
  var _useDerivedState = useDerivedState(useId('accordion-id-'), controlledId),
    _useDerivedState2 = _slicedToArray(_useDerivedState, 1),
    id = _useDerivedState2[0];
  var theme = useTheme();
  var safeProps = safeRest(rest);
  var _safeCustomStyle = safeCustomStyle(customStyle),
    expandablePanelStyle = _safeCustomStyle.expandablePanelStyle;
  var _useAccordion = useAccordion({
      controlledExpanded: controlledExpanded,
      defaultExpanded: defaultExpanded,
      onToggle: onToggle
    }),
    isExpanded = _useAccordion.isExpanded,
    onClickHeader = _useAccordion.onClickHeader;
  var Wrapper;
  if (variant === ACCORDION_VARIANT.SECONDARY) {
    Wrapper = SecondaryAccordionWrapper;
  } else {
    Wrapper = PrimaryAccordionWrapper;
  }
  return /*#__PURE__*/jsx(Wrapper, _objectSpread(_objectSpread({
    id: id,
    "data-testid": id,
    variant: variant,
    expandablePanelStyle: expandablePanelStyle,
    theme: theme
  }, safeProps), {}, {
    children: /*#__PURE__*/jsxs(InnerWrapper, {
      "data-testid": "accordion-inner-wrapper",
      theme: theme,
      variant: variant,
      children: [/*#__PURE__*/jsx(Header, {
        id: "".concat(id, "-header"),
        title: title,
        titleAs: titleAs,
        ariaLabel: ariaLabel,
        ariaControls: "".concat(id, "-body"),
        isExpanded: isExpanded,
        customStyle: {
          headerWrapperStyle: customStyle.headerWrapperStyle,
          titleStyle: customStyle.titleStyle
        },
        variant: variant,
        expandIcon: expandIcon,
        collapseIcon: collapseIcon,
        onClick: onClickHeader
      }), /*#__PURE__*/jsx(Body, {
        id: "".concat(id, "-body"),
        "data-testid": "".concat(id, "-body"),
        isExpanded: isExpanded,
        customStyle: {
          bodyStyle: customStyle.bodyStyle
        },
        variant: variant,
        children: children
      })]
    })
  }));
};
process.env.NODE_ENV !== "production" ? Accordion.propTypes = {
  /** DOM element id */
  id: PropTypes.string,
  /** title label for header */
  title: PropTypes.string,
  /** type of component to use as a title (e.g. `h1`, `h2`, etc) */
  titleAs: PropTypes.string,
  /** onToggle event handler */
  onToggle: PropTypes.func,
  /** aria-label */
  ariaLabel: PropTypes.string,
  /**
   * flag to set the panel to be expanded by default
   *
   * **NOTE**: this prop will be deprecated in v4 and renamed to defaultExpanded
   * */
  expandedByDefault: PropTypes.bool,
  /**
   * User control panel expanded/collapsed state
   *
   * **NOTE**: this prop will be deprecated in v4 and renamed to expanded
   * */
  isExpanded: PropTypes.bool,
  /**
   * Icon displayed in collapsed state.
   *
   * Refer to our documentation on how to pass one of the following icons to this prop:
   *
   * [CDS icons](/docs/icons) /
   * [Manulife icons](/docs/icons#manulife-icons) /
   * [Custom icons](/docs/icons#custom-icons).
   * */
  expandIcon: PropTypes.node,
  /**
   * Icon displayed in expanded state.
   *
   * Refer to our documentation on how to pass one of the following icons to this prop:
   *
   * [CDS icons](/docs/icons) /
   * [Manulife icons](/docs/icons#manulife-icons) /
   * [Custom icons](/docs/icons#custom-icons).
   * */
  collapseIcon: PropTypes.node,
  /** content to display within the expanded area */
  children: PropTypes.node.isRequired,
  /** Which style of accordion to use */
  variant: PropTypes.oneOf([ACCORDION_VARIANT.PRIMARY, ACCORDION_VARIANT.SECONDARY, ACCORDION_VARIANT.LEGAL]),
  /** style overrides */
  customStyle: PropTypes.shape({
    expandablePanelStyle: PropTypes.object,
    // the wrapper for accordion
    headerWrapperStyle: PropTypes.object,
    // the wrapper for title and icon
    titleStyle: PropTypes.object,
    // the title text element
    bodyStyle: PropTypes.object // the wrapper for body
  })
} : undefined;
process.env.NODE_ENV !== "production" ? Accordion.displayName = 'Accordion' : undefined;

export { Accordion as default };
